import axios from 'axios';

// Use current page origin for API base to ensure cookies work across environments.
// Both preview and production route /api/* to the backend on the same domain.
// This prevents cross-origin cookie issues when the build is deployed to production.
const API_BASE = `${window.location.origin}/api`;

const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Auth API
export const authAPI = {
    exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
    getMe: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
    markWelcomeSeen: () => api.post('/auth/welcome-seen'),
    acceptTerms: () => api.post('/auth/accept-terms'),
    register: (data) => api.post('/auth/register', data),
    loginEmail: (data) => api.post('/auth/login-email', data),
};

// Resources API
export const resourcesAPI = {
    getAll: (params = {}) => {
        // Convert boolean params to string for proper query string handling
        const cleanParams = { ...params };
        if (cleanParams.featured_only) {
            cleanParams.featured_only = 'true';
        }
        return api.get('/resources', { params: cleanParams });
    },
    getById: (id) => api.get(`/resources/${id}`),
    create: (data) => api.post('/resources', data),
    update: (id, data) => api.put(`/resources/${id}`, data),
    delete: (id) => api.delete(`/resources/${id}`),
    getStats: () => api.get('/resources/stats'),
    updateMouStatus: (id, mouStatus) => api.put(`/resources/${id}/mou`, { mou_status: mouStatus }),
};

// Submissions API
export const submissionsAPI = {
    create: (data) => api.post('/submissions', data),
    getAll: (params = {}) => api.get('/submissions', { params }),
    getById: (id) => api.get(`/submissions/${id}`),
    approve: (id) => api.post(`/submissions/${id}/approve`),
    reject: (id, notes) => api.post(`/submissions/${id}/reject`, { admin_notes: notes }),
    requestInfo: (id, message) => api.post(`/submissions/${id}/request-info`, { message }),
    reply: (id, message) => api.post(`/submissions/${id}/reply`, { message }),
    getMine: () => api.get('/my-submissions'),
};

// Admin API
export const adminAPI = {
    getResources: (params = {}) => api.get('/admin/resources', { params }),
    getUsers: () => api.get('/admin/users'),
    getMembers: (params = {}) => api.get('/members', { params }),
    makeAdmin: (userId) => api.post(`/admin/make-admin/${userId}`),
    importCSV: (formData) => api.post('/admin/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    uploadPreview: (formData) => api.post('/admin/upload/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    uploadImport: (formData) => api.post('/admin/upload/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    // News/Resources CMS
    getNews: (params = {}) => api.get('/admin/news', { params }),
    createNews: (data) => api.post('/admin/news', data),
    updateNews: (id, data) => api.put(`/admin/news/${id}`, data),
    deleteNews: (id) => api.delete(`/admin/news/${id}`),
    // Organization Editors
    addOrganizationEditor: (resourceId, userId) => api.post(`/admin/organizations/${resourceId}/editors`, { user_id: userId }),
    // Email
    sendTestJourneyEmails: (recipients) => api.post('/admin/test-journey-emails', { recipients }),
};

// User Profile API
export const profileAPI = {
    getMyProfile: () => api.get('/users/me/profile'),
    updateMyProfile: (data) => api.put('/users/me/profile', data),
    getUserProfile: (userId) => api.get(`/users/${userId}/profile`),
    getMembers: (params = {}) => api.get('/members', { params }),
};

// Organizations API (for members creating/editing their orgs)
export const organizationsAPI = {
    create: (data) => api.post('/organizations', data),
    update: (id, data) => api.put(`/organizations/${id}`, data),
    getMyOrganizations: () => api.get('/organizations/my'),
};

// News/Resources API (public)
export const newsAPI = {
    getAll: (params = {}) => api.get('/news', { params }),
    getById: (id) => api.get(`/news/${id}`),
    getCategories: () => api.get('/metadata/news-categories'),
};

// File Upload API
export const uploadAPI = {
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// Metadata API
export const metadataAPI = {
    getOrganizationTypes: () => api.get('/metadata/organization-types'),
    getStakeholderTypes: () => api.get('/metadata/stakeholder-types'),
    getStates: () => api.get('/metadata/states'),
    getMouStatuses: () => api.get('/metadata/mou-statuses'),
    getNewsCategories: () => api.get('/metadata/news-categories'),
    getDocumentTypes: () => api.get('/metadata/document-types'),
};

// Annual Metrics API
export const metricsAPI = {
    getOrgMetrics: (resourceId) => api.get(`/organizations/${resourceId}/metrics`),
    submitMetrics: (resourceId, data) => api.post(`/organizations/${resourceId}/metrics`, data),
    getImpactHistory: () => api.get('/impact/history'),
    // Admin
    getAllSubmissions: (params = {}) => api.get('/admin/metrics', { params }),
    approve: (metricsId) => api.put(`/admin/metrics/${metricsId}/approve`),
    reject: (metricsId) => api.put(`/admin/metrics/${metricsId}/reject`),
};

// Organization Claims API
export const claimsAPI = {
    submitClaim: (resourceId, data) => api.post(`/organizations/${resourceId}/claim`, data),
    getClaimStatus: (resourceId) => api.get(`/organizations/${resourceId}/claim-status`),
    // Admin
    getAllClaims: (params = {}) => api.get('/admin/claims', { params }),
    approveClaim: (claimId, data = {}) => api.put(`/admin/claims/${claimId}/approve`, data),
    rejectClaim: (claimId, data = {}) => api.put(`/admin/claims/${claimId}/reject`, data),
    assignOrg: (resourceId, data) => api.post(`/admin/organizations/${resourceId}/assign`, data),
    getEditors: (resourceId) => api.get(`/admin/organizations/${resourceId}/editors`),
    removeEditor: (resourceId, userId) => api.delete(`/admin/organizations/${resourceId}/editors/${userId}`),
};

// Digital Binder API
export const binderAPI = {
    // Binders
    getBinders: () => api.get('/binders'),
    getBinder: (binderId) => api.get(`/binders/${binderId}`),
    createBinder: (data) => api.post('/binders', data),
    updateBinder: (binderId, data) => api.put(`/binders/${binderId}`, data),
    deleteBinder: (binderId) => api.delete(`/binders/${binderId}`),
    
    // Documents
    uploadDocument: (binderId, data) => api.post(`/binders/${binderId}/documents`, data),
    getDocument: (binderId, documentId) => api.get(`/binders/${binderId}/documents/${documentId}`),
    updateDocument: (binderId, documentId, data) => api.put(`/binders/${binderId}/documents/${documentId}`, data),
    deleteDocument: (binderId, documentId) => api.delete(`/binders/${binderId}/documents/${documentId}`),
    
    // Sharing
    createShareLink: (binderId, data) => api.post(`/binders/${binderId}/share`, data),
    getShareLinks: (binderId) => api.get(`/binders/${binderId}/shares`),
    revokeShareLink: (shareId) => api.delete(`/shares/${shareId}`),
    
    // Saved Resources
    saveResource: (binderId, data) => api.post(`/binders/${binderId}/resources`, data),
    saveBookmark: (binderId, data) => api.post(`/binders/${binderId}/bookmarks`, data),
    getResources: (binderId) => api.get(`/binders/${binderId}/resources`),
    removeResource: (binderId, resourceId) => api.delete(`/binders/${binderId}/resources/${resourceId}`),
    removeBookmark: (binderId, savedId) => api.delete(`/binders/${binderId}/bookmarks/${savedId}`),
    
    // Public shared access
    accessSharedBinder: (shareId, data) => api.post(`/shared/${shareId}/access`, data),
    getSharedDocument: (shareId, documentId, password) => api.get(`/shared/${shareId}/document/${documentId}`, { params: { password } }),
};

export default api;
